import { Button, Chip, TextLoadingPlaceholder } from '@app/core';
import React, { useState, useMemo } from 'react';
import classNames from 'classnames';
import { createUseStyles } from 'react-jss';
import { Colors, Theme } from '@app/theme';
import { Checkbox } from '@app/form';
import {
  DeclinedReason,
  FieldChoice,
  FieldType,
  FilterType,
  ParticipantFieldChoice,
  ParticipantFieldType,
  ParticipationStatus,
  ParticipationTag,
  StudyType,
  useSessionParticipationsFilterableValues,
  FilterableValue,
  CustomSelectChoice,
} from '@app/api';
import { t } from 'i18next';
import { getColorForParticipationStatus } from 'lib/participationUtils';
import { OldValuesList } from './OldValuesList';
import { FilterPopover } from 'components/ParticipationsTable';

export const formatTagFieldChoice = (choice: ParticipationTag): ChoiceValue => {
  return {
    label: choice.name,
    value: choice.id as string,
    color: choice.color,
  };
};

export const formatFieldChoice = (
  choice: FieldChoice | ParticipantFieldChoice,
): ChoiceValue => {
  const isParticipantChoice = isParticipantFieldChoice(choice);
  const value = isParticipantChoice
    ? choice.value
    : choice.ref === CustomSelectChoice.Other
    ? choice.ref
    : choice.label;
  return {
    label: choice.label,
    value: value as string,
  };
};

export const formatStatusChoice = (
  choice: ParticipationStatus,
  studyType: StudyType,
): ChoiceValue => {
  return {
    label: t(`sessions.details.monitoring.status.${choice}`, {
      context: studyType.toLowerCase(),
    }),
    value: choice,
    color: getColorForParticipationStatus(choice),
  };
};

export const formatDeclinedReasonChoice = (
  choice: DeclinedReason,
): ChoiceValue => {
  return {
    label: t(`sessions.details.participationsDetails.declinedReason.${choice}`),
    value: choice,
    color: 'red',
  };
};

export const isParticipantFieldChoice = (
  choice: FieldChoice | ParticipantFieldChoice | ParticipationTag,
): choice is ParticipantFieldChoice => {
  return 'value' in choice;
};

const useStyles = createUseStyles((theme: Theme) => ({
  choiceList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.marginBase / 2,
  },
  button: {
    ...theme.fonts.caption,
    padding: 0,
    height: theme.iconSize.regular,
    width: theme.iconSize.regular,
  },
  buttonIcon: {
    fontSize: theme.iconSize.large / 2,
  },
  boldText: {
    ...theme.fonts.label,
    color: theme.colors.inkSecondary,
  },
}));

export interface ChoiceValue {
  label: string;
  value: string | number;
  color?: Colors;
}

interface Props {
  fieldRef?: string;
  fieldLabel?: string;
  fieldType?: FieldType | ParticipantFieldType;
  values?: ChoiceValue[];
  selectedValues?: string[];
  sessionId?: string;
  onFilterDataChange?: (
    field: string,
    type: FilterType,
    values: string[],
  ) => void;
  textStyle?: 'normal' | 'bold';
  loadOnlyUsedValues?: boolean;
  loadOldValues?: boolean;
}
export const FieldFilterButton = ({
  fieldRef,
  fieldLabel,
  fieldType,
  values,
  selectedValues = [],
  sessionId,
  onFilterDataChange,
  textStyle,
  loadOnlyUsedValues,
  loadOldValues,
}: Props) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | undefined>(
    undefined,
  );
  const [localSelectedValues, setLocalSelectedValues] = useState<
    string[] | undefined
  >(undefined);
  const [shouldFetchUsedValues, setShouldFetchUsedValues] = useState(false);
  const [shouldFetchOldValues, setShouldFetchOldValues] = useState(false);

  const shouldLoadUsedValues = loadOnlyUsedValues && sessionId && fieldRef;
  const {
    data: filterableUsedValues,
    isLoading: isLoadingFilterableUsedValues,
  } = useSessionParticipationsFilterableValues(
    shouldLoadUsedValues && shouldFetchUsedValues ? sessionId : undefined,
    shouldLoadUsedValues && shouldFetchUsedValues && fieldRef
      ? fieldRef
      : undefined,
  );
  const { data: oldValues, isLoading: isLoadingOldValues } =
    useSessionParticipationsFilterableValues(
      shouldFetchOldValues ? sessionId : undefined,
      shouldFetchOldValues && fieldRef ? fieldRef : undefined,
      true,
    );

  const displayValues = useMemo(() => {
    if (!shouldLoadUsedValues || !filterableUsedValues || !values) {
      return values || [];
    }

    if (!filterableUsedValues) {
      return values;
    }

    const availableValues = new Set(
      filterableUsedValues.values.map((v: FilterableValue) => String(v.value)),
    );

    return values
      .filter((choice) => availableValues.has(String(choice.value)))
      .sort((a, b) =>
        String(a.label ?? a.value).localeCompare(String(b.label ?? b.value)),
      );
  }, [filterableUsedValues, values, shouldLoadUsedValues]);

  const hasSelection =
    localSelectedValues?.length || selectedValues?.length > 0;

  const hasOldValueSelected = useMemo(() => {
    if (!loadOldValues || !hasSelection) return false;

    const displaySet = new Set(displayValues?.map((v) => String(v.value)));

    return selectedValues?.some((v) => !displaySet.has(String(v))) ?? false;
  }, [loadOldValues, hasSelection, selectedValues, displayValues]);

  if (
    !values?.length ||
    values.length <= 1 ||
    (fieldType !== FieldType.SINGLE_CHOICE &&
      fieldType !== FieldType.MULTIPLE_CHOICE &&
      fieldType !== ParticipantFieldType.SINGLE_CHOICE &&
      fieldType !== ParticipantFieldType.MULTIPLE_CHOICE &&
      fieldType !== ParticipantFieldType.TAGS &&
      fieldType !== ParticipantFieldType.LOCALES)
  ) {
    return null;
  }

  const openPopover = (event: React.MouseEvent<HTMLButtonElement>) => {
    setLocalSelectedValues(selectedValues);
    setAnchorEl(event.currentTarget);

    if (loadOldValues && !shouldFetchOldValues) {
      setShouldFetchOldValues(true);
    }

    if (shouldLoadUsedValues && !shouldFetchUsedValues) {
      setShouldFetchUsedValues(true);
    }
  };

  const closePopover = () => {
    setAnchorEl(undefined);
  };

  const isChecked = (value: string) => localSelectedValues?.includes(value);
  const handleCheckboxChange = (value: string, checked: boolean) => {
    const base = localSelectedValues || [];
    const newSelected = checked
      ? [...base, value]
      : localSelectedValues?.filter((v) => v !== value);

    setLocalSelectedValues(newSelected);
  };

  const handleApply = () => {
    if (fieldRef && onFilterDataChange) {
      onFilterDataChange(
        fieldRef,
        FilterType.CHOICE,
        localSelectedValues || [],
      );
    }
    setLocalSelectedValues(undefined);
    closePopover();
  };

  const handleClearAll = () => {
    if (fieldRef && onFilterDataChange) {
      onFilterDataChange(fieldRef, FilterType.CHOICE, []);
    }
    setLocalSelectedValues([]);
    closePopover();
  };

  return (
    <>
      <Button
        variant="muted"
        color={hasSelection ? 'blue' : 'ink'}
        contentClassName={classes.button}
        xsmall
        onClick={openPopover}
      >
        <i className={classNames('ri-filter-3-line', classes.buttonIcon)} />
      </Button>
      <FilterPopover
        anchorEl={anchorEl}
        onRequestClose={closePopover}
        title={fieldLabel}
        onClear={hasSelection ? handleClearAll : undefined}
        onApply={handleApply}
      >
        <div className={classes.choiceList}>
          {isLoadingFilterableUsedValues
            ? Array.from({ length: 5 }).map(() => (
                <TextLoadingPlaceholder sample="Lorem ipsum dolor sit" />
              ))
            : displayValues.map((v) => {
                const choiceValue = String(v.value);
                return (
                  <Checkbox
                    checked={isChecked(choiceValue)}
                    noMargin
                    labelColorChange
                    onChange={(e) =>
                      handleCheckboxChange(choiceValue, e.target.checked)
                    }
                  >
                    {v.color ? (
                      <Chip color={v.color}>{v.label} </Chip>
                    ) : (
                      <span
                        className={classNames({
                          [classes.boldText]: textStyle === 'bold',
                        })}
                      >
                        {v.label}
                      </span>
                    )}
                  </Checkbox>
                );
              })}
          {loadOldValues &&
            (isLoadingOldValues ? (
              <TextLoadingPlaceholder sample="Lorem ipsum dolor" />
            ) : (
              <OldValuesList
                oldValues={oldValues?.values || undefined}
                handleCheckboxChange={handleCheckboxChange}
                isChecked={isChecked}
                hasSelection={hasOldValueSelected}
                displayOther={
                  !displayValues.some(
                    (v) => v.value === CustomSelectChoice.Other,
                  )
                }
              />
            ))}
        </div>
      </FilterPopover>
    </>
  );
};
